import type { RTCSession } from 'jssip/lib/RTCSession';

import type { AppDispatch } from 'features/app/store/store';
import {
  sendSessionAcceptedAnalytics,
  sendSessionDisconnectedAnalytics,
  sendSessionForceClosedAnalytics,
  trackAllSessionsDisconnectedAnalytics,
  stopAllMediaTracks,
  clearMediaStreams,
  isSessionCompletelyDisconnected,
} from 'features/sip/helpers';

type SessionRole = 'direct' | 'teaming' | 'wavello';

/**
 * Service to track RTC sessions during teaming and Wavello operations.
 *
 * During teaming, we maintain references to both the direct (original) session
 * and the teaming session to handle proper event routing and cleanup.
 * For Wavello calls, we track the wavello session separately since it has
 * different lifecycle management - it should not interfere with other sessions
 * when replaced, but should be cleaned up during complete disconnection.
 * This is essential for correct deaf disconnect handling and session management.
 */
class RtcSessionTrackerService {
  // ========================================
  // PRIVATE PROPERTIES
  // ========================================

  private directSession: RTCSession | null = null;
  private teamingSession: RTCSession | null = null;
  private wavelloSession: RTCSession | null = null;
  private dispatch: AppDispatch | null = null;
  private readonly nonActiveSessions = new Set<RTCSession>();
  // ========================================
  // PUBLIC API - SESSION MANAGEMENT
  // ========================================

  /**
   * Set the dispatch function for analytics. Pass null to clear.
   */
  setDispatch(dispatch: AppDispatch | null): void {
    this.dispatch = dispatch;
  }

  /**
   * Automatically track a session based on its remote URI.
   * Sessions with 'SVRS-RT' in their URI are teaming sessions,
   * sessions with 'SVRS-DHV-' in their URI are Wavello sessions,
   * all others are direct sessions.
   *
   * @param session - The RTCSession to track
   * @param dispatch - Optional dispatch function. If provided, will be set and used for analytics
   */
  trackSession(session: RTCSession, dispatch?: AppDispatch | null): void {
    if (dispatch !== undefined) {
      this.setDispatch(dispatch);
    }

    const uri = this.getSessionUri(session);
    let sessionRole: SessionRole;

    if (this.isWavelloUri(uri)) {
      sessionRole = 'wavello';
    } else if (this.isTeamingUri(uri)) {
      sessionRole = 'teaming';
    } else {
      sessionRole = 'direct';
    }

    this.safelyReplaceSession(sessionRole, session);

    // Track analytics after successful session tracking
    if (this.dispatch) {
      sendSessionAcceptedAnalytics(this.dispatch, sessionRole, session);
    }
  }
  // ========================================
  // PUBLIC API - SESSION QUERIES
  // ========================================

  /**
   * Get the current direct session.
   */
  getDirectSession(): RTCSession | null {
    return this.directSession;
  }

  /**
   * Get the current teaming session.
   */
  getTeamingSession(): RTCSession | null {
    return this.teamingSession;
  }

  /**
   * Get the current Wavello session.
   */
  getWavelloSession(): RTCSession | null {
    return this.wavelloSession;
  }

  /**
   * Check if the given session is the currently tracked direct session.
   */
  isDirectSession(session: RTCSession | null | undefined): boolean {
    return this.isSameSession(session, this.directSession);
  }

  /**
   * Check if the given session is the currently tracked teaming session.
   */
  isTeamingSession(session: RTCSession | null | undefined): boolean {
    return this.isSameSession(session, this.teamingSession);
  }

  /**
   * Check if the given session is the currently tracked Wavello session.
   */
  isWavelloSession(session: RTCSession | null | undefined): boolean {
    return this.isSameSession(session, this.wavelloSession);
  }

  /**
   * Check if any sessions are currently being tracked.
   */
  hasActiveSessions(): boolean {
    return (
      this.directSession !== null ||
      this.teamingSession !== null ||
      this.wavelloSession !== null
    );
  }

  /**
   * Determine if a URI belongs to a teaming session.
   */
  isTeamingUri(uri: string): boolean {
    return uri.toLowerCase().includes('svrs-rt');
  }

  /**
   * Determine if a URI belongs to a Wavello session.
   */
  isWavelloUri(uri: string): boolean {
    return uri.toLowerCase().includes('svrs-dhv-');
  }
  // ========================================
  // PUBLIC API - CLEANUP & DISCONNECTION
  // ========================================

  /**
   * Disconnect and clear all tracked sessions, ensuring they are fully terminated first.
   * This guarantees no lingering connections or media tracks remain.
   */
  disconnectAndClearAll(): void {
    // Early return if there are no sessions to disconnect
    if (
      !this.directSession &&
      !this.teamingSession &&
      !this.wavelloSession &&
      this.nonActiveSessions.size === 0
    ) {
      return;
    }

    const directSessionUri = this.getSessionUri(this.directSession);
    const teamingSessionUri = this.getSessionUri(this.teamingSession);
    const nonActiveSessionCount = this.nonActiveSessions.size;

    this.performDisconnection(this.directSession);
    this.performDisconnection(this.teamingSession);
    this.performDisconnection(this.wavelloSession);

    // DOUBLE SECURITY: Ensure any non-active sessions are REALLY gone
    // This is defensive programming to guarantee nothing escapes cleanup
    this.nonActiveSessions.forEach((session) => {
      this.performDisconnection(session);
    });

    // Clear all references
    this.directSession = null;
    this.teamingSession = null;
    this.wavelloSession = null;
    this.nonActiveSessions.clear();

    // Track analytics for the complete cleanup operation
    if (this.dispatch) {
      trackAllSessionsDisconnectedAnalytics(
        this.dispatch,
        directSessionUri,
        teamingSessionUri,
        nonActiveSessionCount
      );
    }
  }

  /**
   * Disconnect and clear the direct session only.
   */
  disconnectAndClearDirectSession(): void {
    if (this.directSession) {
      this.nonActiveSessions.add(this.directSession);
      this.performDisconnection(this.directSession);
      this.directSession = null;
    }
  }
  // ========================================
  // PRIVATE SESSION MANAGEMENT
  // ========================================

  /**
   * Safely replace a session of the specified type, ensuring proper disconnection behavior.
   *
   * For Wavello sessions: Only disconnect existing Wavello sessions, do not touch direct/teaming sessions.
   * For Direct sessions: Only disconnect existing direct sessions, do not touch teaming/wavello sessions.
   * For Teaming sessions: Only disconnect existing teaming sessions, do not touch direct/wavello sessions.
   *
   * @param sessionType - The type of session to replace ('direct', 'teaming', or 'wavello')
   * @param newSession - The new session to track
   */
  private safelyReplaceSession(
    sessionType: SessionRole,
    newSession: RTCSession
  ): void {
    if (sessionType === 'wavello') {
      // For Wavello sessions, only disconnect existing Wavello sessions
      if (
        this.wavelloSession &&
        !this.isSameSession(this.wavelloSession, newSession)
      ) {
        this.disconnectSession(this.wavelloSession, newSession);
        this.wavelloSession = null;
      }
    } else if (sessionType === 'teaming') {
      // For teaming sessions, only disconnect existing teaming sessions
      if (
        this.teamingSession &&
        !this.isSameSession(this.teamingSession, newSession)
      ) {
        this.disconnectSession(this.teamingSession, newSession);
        this.teamingSession = null;
      }
    } else if (sessionType === 'direct') {
      // For direct sessions, only disconnect existing direct sessions
      if (
        this.directSession &&
        !this.isSameSession(this.directSession, newSession)
      ) {
        this.disconnectSession(this.directSession, newSession);
        this.directSession = null;
      }
    }

    // Set the new session
    this.setSessionByType(sessionType, newSession);
  }

  /**
   * Disconnect a single session and handle all associated logging and analytics.
   */
  private disconnectSession(
    sessionToDisconnect: RTCSession,
    newSession: RTCSession
  ): void {
    const oldUri = this.getSessionUri(sessionToDisconnect);
    const oldSessionRole = this.determineSessionRole(sessionToDisconnect);
    const isOldSessionDisconnected =
      isSessionCompletelyDisconnected(sessionToDisconnect);

    // Log the disconnection
    if (!isOldSessionDisconnected) {
      console.warn(
        `Old ${oldSessionRole} session appears active, forcing disconnection: ${oldUri}`
      );

      // Track analytics for sessions that appeared active when replaced
      if (this.dispatch) {
        sendSessionForceClosedAnalytics({
          dispatch: this.dispatch,
          sessionRole: oldSessionRole || 'unknown',
          oldSession: sessionToDisconnect,
          newSession,
        });
      }
    } else {
      console.log(
        `Old ${oldSessionRole} session appears already disconnected: ${oldUri}`
      );
    }

    // Always disconnect the session regardless of its apparent state
    this.performDisconnection(sessionToDisconnect);

    // Add to non-active sessions for double security cleanup later
    this.nonActiveSessions.add(sessionToDisconnect);
  }

  /**
   * Fully disconnect a session: terminate signaling and stop all media tracks.
   * This method GUARANTEES that no session can survive the disconnection process.
   */
  private performDisconnection(session: RTCSession | null): void {
    if (!session) {
      return;
    }

    const uri = this.getSessionUri(session);
    const sessionRole = this.determineSessionRole(session);

    // PHASE 1: Terminate the JsSIP session
    // Always attempt termination regardless of state - some sessions may appear ended but still have resources
    if (typeof session.terminate === 'function') {
      try {
        session.terminate();
      } catch (_e) {
        // Ignore errors - we'll clean up the underlying connection anyway
      }
    }

    // PHASE 2: destroy the RTCPeerConnection and all associated resources
    const pc = session.connection;
    if (pc) {
      try {
        // Stop all tracks BEFORE closing the connection to ensure proper cleanup
        stopAllMediaTracks(pc);

        // Clear all MediaStreams that might still be referenced
        clearMediaStreams(pc);

        // CRITICAL: Close the RTCPeerConnection - this sets signaling state to 'closed'
        // and terminates ICE agent, ending any ongoing ICE processing and active streams
        if (typeof pc.close === 'function') {
          pc.close();
        }
      } catch (error) {
        console.warn(
          `Error during RTCPeerConnection cleanup for ${uri}:`,
          error
        );
      }
    }

    // PHASE 3: Remove all event listeners to prevent memory leaks and lingering references
    if (typeof session.removeAllListeners === 'function') {
      try {
        session.removeAllListeners();
      } catch (_e) {
        // Some implementations may not support this
      }
    }

    // PHASE 4: Dispatch disconnection analytics to guarantee session closure was tracked
    if (this.dispatch) {
      sendSessionDisconnectedAnalytics(this.dispatch, sessionRole, session);
    }
  }

  // ========================================
  // PRIVATE UTILITY HELPERS
  // ========================================

  /**
   * Set a session by its type (direct, teaming, or wavello).
   */
  private setSessionByType(
    sessionType: SessionRole,
    session: RTCSession | null
  ): void {
    if (sessionType === 'teaming') {
      this.teamingSession = session;
    } else if (sessionType === 'wavello') {
      this.wavelloSession = session;
    } else {
      this.directSession = session;
    }
  }

  /**
   * Determine the role of a given session.
   */
  private determineSessionRole(session: RTCSession): SessionRole | undefined {
    if (this.isDirectSession(session)) {
      return 'direct';
    } else if (this.isTeamingSession(session)) {
      return 'teaming';
    } else if (this.isWavelloSession(session)) {
      return 'wavello';
    }
    return undefined;
  }

  /**
   * Extract URI from a session safely.
   */
  private getSessionUri(session: RTCSession | null): string {
    return session?.remote_identity?.uri?.toString() || '';
  }

  /**
   * Check if two sessions are the same instance.
   */
  private isSameSession(
    session1: RTCSession | null | undefined,
    session2: RTCSession | null
  ): boolean {
    if (!session1 || !session2) {
      return false;
    }
    return session1 === session2;
  }
}

// Export a singleton instance
export const RtcSessionTracker = new RtcSessionTrackerService();
